import { useEffect, useState } from "react";
import api from "../api";
import Layout from "../components/Layout";

/**
 * Reglages des alertes e-mail, cote client.
 *
 * L'ecran ne montre que ce que le client peut changer : les coordonnees de son
 * entreprise et ses alertes. Son abonnement, ses modules, sa raison sociale et
 * ses couleurs se reglent cote plateforme : les melanger ici ferait une page ou
 * l'on ne sait plus ce qui nous appartient.
 *
 * LES COORDONNEES (adresse, contact, telephone, SIRET) figurent sur les documents
 * du client : en-tete et pied des rapports, contrats de location. Le client les
 * saisit UNE FOIS ici, et tous les documents les reprennent -- ils n'avaient
 * aucun ecran, donc personne ne pouvait les remplir sans l'editeur.
 *
 * Le point important est le rappel des DESTINATAIRES REELS. Sans lui, un
 * client laisse le champ vide en croyant avoir coupe les alertes, alors
 * qu'elles partent a tous ses administrateurs.
 */
const CHAMPS_ENTREPRISE = [
  ["adresse", "Adresse"],
  ["code_postal", "Code postal"],
  ["ville", "Ville"],
  ["contact", "Contact"],
  ["telephone", "Téléphone"],
  ["siret", "SIRET"],
];

export default function Reglages({ user, onLogout }) {
  const [entreprise, setEntreprise] = useState(null); // ce que le serveur a enregistré
  const [saisie, setSaisie] = useState({});
  const [erreurEntreprise, setErreurEntreprise] = useState("");
  const [messageEntreprise, setMessageEntreprise] = useState("");
  const [envoiEntreprise, setEnvoiEntreprise] = useState(false);
  const [reglages, setReglages] = useState(null);
  const [email, setEmail] = useState("");
  const [actives, setActives] = useState(true);
  const [erreur, setErreur] = useState("");
  const [message, setMessage] = useState("");
  const [envoi, setEnvoi] = useState(false);

  useEffect(() => {
    api
      .get("/reglages/entreprise")
      .then(({ data }) => {
        setEntreprise(data);
        setSaisie(Object.fromEntries(CHAMPS_ENTREPRISE.map(([c]) => [c, data[c] || ""])));
      })
      .catch(() => setErreurEntreprise("Impossible de charger les coordonnées de l'entreprise."));
  }, []);

  const enregistrerEntreprise = () => {
    setEnvoiEntreprise(true);
    setErreurEntreprise("");
    setMessageEntreprise("");
    api
      .patch("/reglages/entreprise", saisie)
      .then(({ data }) => {
        setEntreprise(data);
        setSaisie(Object.fromEntries(CHAMPS_ENTREPRISE.map(([c]) => [c, data[c] || ""])));
        setMessageEntreprise("Coordonnées enregistrées.");
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        // Une erreur de validation arrive en liste : on dit CE QUI est faux, pas un
        // « enregistrement impossible » qui renverrait chercher une panne.
        setErreurEntreprise(
          Array.isArray(detail) && detail[0]?.msg
            ? detail[0].msg.replace(/^Value error, /, "")
            : typeof detail === "string"
            ? detail
            : "Enregistrement impossible."
        );
      })
      .finally(() => setEnvoiEntreprise(false));
  };

  const entrepriseModifiee =
    entreprise &&
    CHAMPS_ENTREPRISE.some(([c]) => (saisie[c] || "").trim() !== (entreprise[c] || ""));

  useEffect(() => {
    api
      .get("/reglages/alertes")
      .then(({ data }) => {
        setReglages(data);
        setEmail(data.email_alertes || "");
        setActives(data.alertes_actives);
      })
      .catch(() => setErreur("Impossible de charger les réglages."));
  }, []);

  const enregistrer = () => {
    setEnvoi(true);
    setErreur("");
    setMessage("");
    api
      .patch("/reglages/alertes", {
        alertes_actives: actives,
        // Chaine vide volontaire : c'est ainsi qu'on efface l'adresse dédiée
        // et qu'on revient aux administrateurs.
        email_alertes: email.trim(),
      })
      .then(({ data }) => {
        setReglages(data);
        setEmail(data.email_alertes || "");
        setActives(data.alertes_actives);
        setMessage("Réglages enregistrés.");
      })
      .catch((err) => {
        const detail = err?.response?.data?.detail;
        setErreur(
          typeof detail === "string"
            ? detail
            : "Adresse e-mail invalide ou enregistrement impossible."
        );
      })
      .finally(() => setEnvoi(false));
  };

  const destinataires = reglages?.destinataires_effectifs ?? [];
  const modifie =
    reglages &&
    (actives !== reglages.alertes_actives ||
      email.trim() !== (reglages.email_alertes || ""));

  return (
    <Layout user={user} onLogout={onLogout}>
      <h2>Réglages</h2>
      {erreur && <p className="erreur">{erreur}</p>}
      {message && <p className="note-succes">{message}</p>}

      <section className="carte carte-etroite" data-testid="entreprise">
        <h3>Mon entreprise</h3>
        <p className="note-info">
          Ces informations figurent sur vos documents : en-tête et pied des
          rapports, contrats de mise à disposition. Saisissez-les une fois, elles
          sont reprises partout. Les documents déjà signés ne changent pas.
        </p>
        {entreprise && (
          <p className="note-recap">
            Raison sociale : <strong>{entreprise.nom}</strong>{" "}
            <span className="note-discrete">(pour la modifier, contactez Ridelle)</span>
          </p>
        )}
        {erreurEntreprise && <p className="erreur">{erreurEntreprise}</p>}
        {messageEntreprise && <p className="note-succes">{messageEntreprise}</p>}
        {entreprise &&
          CHAMPS_ENTREPRISE.map(([cle, libelle]) => (
            <label className="modal-champ" key={cle}>
              {libelle}
              <input
                type="text"
                value={saisie[cle] || ""}
                inputMode={cle === "siret" ? "numeric" : undefined}
                placeholder={cle === "siret" ? "14 chiffres" : undefined}
                onChange={(e) => setSaisie({ ...saisie, [cle]: e.target.value })}
              />
            </label>
          ))}
        <div className="modal-actions">
          <button
            className="btn-accent"
            onClick={enregistrerEntreprise}
            disabled={envoiEntreprise || !entrepriseModifiee}
          >
            {envoiEntreprise ? "…" : "Enregistrer les coordonnées"}
          </button>
        </div>
      </section>

      <section className="carte carte-etroite">
        <h3>Alertes par e-mail</h3>
        <p className="note-info">
          Un e-mail part dès qu'une échéance approche ou qu'elle est dépassée,
          puis une fois par semaine tant qu'elle n'est pas traitée. Un seul
          message par jour, jamais un par véhicule.
        </p>

        <label className="champ-interrupteur">
          <input
            type="checkbox"
            checked={actives}
            onChange={(e) => setActives(e.target.checked)}
          />
          <span>Recevoir les alertes</span>
        </label>

        <label className="modal-champ">
          Envoyer les alertes à
          {/* `type="text"` et non `type="email"` : le navigateur refuserait
              une liste séparée par des virgules et bloquerait la saisie avant
              même que le serveur la voie. C'est LUI qui valide chaque adresse
              (`ReglagesAlertesUpdate`), et il le fait pour toutes, pas
              seulement la première. */}
          <input
            type="text"
            placeholder="exploitation@votre-entreprise.fr, adjoint@…"
            value={email}
            disabled={!actives}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <p className="note-info">
          <strong>Plusieurs adresses possibles</strong>, séparées par des
          virgules — pratique pour prévenir aussi un adjoint pendant vos
          congés.
        </p>
        <p className="note-info">
          Laissez vide pour prévenir tous les administrateurs. Une boîte
          partagée est souvent plus sûre : si la seule personne prévenue est en
          congés, l'échéance passe.
        </p>

        {/* Ce que le client recevra VRAIMENT. Le champ vide ne veut pas dire
            « personne » — le dire evite un malentendu couteux. */}
        {actives && (
          <p className="note-recap">
            {destinataires.length > 0 ? (
              <>
                Aujourd'hui, les alertes partent à :{" "}
                <strong>{destinataires.join(", ")}</strong>
              </>
            ) : (
              <>
                <strong>Aucun destinataire.</strong> Renseignez une adresse
                ci-dessus, sinon personne ne sera prévenu.
              </>
            )}
          </p>
        )}
        {!actives && (
          <p className="note-recap">
            Les alertes sont <strong>coupées</strong> : aucun e-mail ne partira.
            Les échéances restent visibles sur le tableau de bord.
          </p>
        )}

        {/* LE BOUTON VIT AVEC LES CHAMPS QU'IL ENREGISTRE. Il était sous la
            carte suivante, qui ne porte plus rien de modifiable depuis le
            retrait de l'interrupteur : il aurait donné l'impression de régler
            la lecture de factures. */}
        <div className="modal-actions">
          <button
            className="btn-accent"
            onClick={enregistrer}
            disabled={envoi || !modifie}
          >
            {envoi ? "…" : "Enregistrer"}
          </button>
        </div>
      </section>
    </Layout>
  );
}
